// File: js/ui/engine-messages.js
//
// Every refusal the money engine can produce, in French a cashier can act on.
//
// Invariant 6 says a failure must reach a human. Invariant 7 says validation
// REFUSES with a kebab-case code rather than raising. This module is the last leg
// of that chain: it turns the code into a sentence that tells the person at the
// desk what to DO. It is the only place French error copy lives, so no screen can
// invent its own wording for a rule that fired somewhere else.
//
// TWO RULES THIS FILE LIVES UNDER
//
//   1. EVERY code the validation path can return has an entry. The unit test
//      PARSES `supabase/migrations/*.sql` and fails when a code appears there with
//      no message here, so a rule added server-side cannot ship as a blank screen.
//
//   2. An UNMAPPED code still names itself. The fallback is never « une erreur est
//      survenue » — that is precisely the pre-rewrite behaviour where a
//      unique-constraint violation was rendered as a green « Transaction saved
//      offline » (audit C1) and nobody could tell which rule had fired. A code we
//      have no copy for is shown verbatim so the admin can look it up.
//
// The strings marked (spec §9) are quoted from the specification and must not be
// reworded without changing the spec.

/**
 * The §9 strings, verbatim. Exported as constants so a screen quotes the spec
 * rather than paraphrasing it.
 */
export const UI_STRINGS = Object.freeze({
    /** A sale that is on this device but not yet on the server. */
    SAVED_OFFLINE: 'Enregistré sur cet appareil — sera synchronisé dès le retour du réseau.',
    /** Printed next to every receipt number we issue. */
    RECEIPT_ISSUED: 'Inscrivez ce numéro dans le carnet de reçus papier.',
    /** No number left in this device\'s block. */
    BLOCK_EXHAUSTED: 'Plus de numéros de reçu — utilisez le carnet papier.',
    /** The browser will not promise to keep what we store. */
    STORAGE_NOT_DURABLE: 'Cet appareil ne peut pas conserver les paiements en sécurité — utilisez le carnet papier aujourd\'hui.'
});

/**
 * « À la connaissance de cet appareil (mis à jour {t}) » (spec §9).
 * @param {string|null} when Human-readable instant, or null when never pulled.
 * @returns {string}
 */
export function stalenessNotice(when) {
    return `À la connaissance de cet appareil (mis à jour ${when || 'jamais'})`;
}

/**
 * « {n} vente(s) non encore synchronisée(s) sont incluses dans ce montant » (spec §9).
 *
 * Queued offline money MUST appear in totals carrying this caveat (audit H6): a
 * cashier who worked an offline morning must not read « Recettes du jour : 0 FCFA »
 * while holding a full drawer.
 *
 * @param {number} n
 * @returns {string} Empty string when nothing is pending — no caveat, no clutter.
 */
export function pendingInTotalNotice(n) {
    if (!n) return '';
    return `${n} vente(s) non encore synchronisée(s) sont incluses dans ce montant`;
}

/**
 * « {n} enregistrement(s) nécessitent votre attention » (spec §9).
 * @param {number} n
 * @returns {string} Empty string when the queue is clear.
 */
export function conflictQueueNotice(n) {
    if (!n) return '';
    return `${n} enregistrement(s) nécessitent votre attention`;
}

/**
 * Codes that carry a variable tail, `prefix:detail`. Handled before the exact map
 * so `server-error: division by zero` never falls through to the generic wording.
 */
const PREFIX_MESSAGES = Object.freeze({
    // 0004: a unique_violation whose CONSTRAINT_NAME is none of the three the
    // validation path names explicitly. Unknown collision, real money.
    'unique-violation': (detail) =>
        `Doublon refusé par le serveur (contrainte : ${detail}). L'enregistrement est en attente de décision — prévenez l'administrateur.`,
    // 0005 sync_push: apply_money_event raised instead of returning a verdict.
    'server-error': (detail) =>
        `Erreur interne du serveur pendant l'enregistrement (${detail}). La vente est conservée sur cet appareil et placée en file d'attente — prévenez l'administrateur.`,
    // db/sync.js: the server answered with a status invariant 6 says cannot exist.
    'unknown-ack-status': (detail) =>
        `Réponse inattendue du serveur (« ${detail} »). La vente est conservée et placée en file d'attente — prévenez l'administrateur.`
});

/**
 * Exact-match catalogue. Grouped by where the code is raised so a reader can find
 * the SQL that produced it.
 */
const MESSAGES = Object.freeze({
    // ---- client-side collection gate (spec §7) -----------------------------
    'storage-not-persistent': UI_STRINGS.STORAGE_NOT_DURABLE,
    'device-not-provisioned':
        'Cet appareil n\'est pas encore enregistré — un administrateur doit l\'enregistrer avant toute encaissement. Utilisez le carnet de reçus papier en attendant.',
    'no-receipt-numbers': UI_STRINGS.BLOCK_EXHAUSTED,
    'engine-not-ready':
        'Le module d\'encaissement n\'est pas encore prêt. Attendez quelques secondes, puis réessayez ; si le message persiste, rechargez la page.',

    // ---- local write refusals (db/errors.js) -------------------------------
    validation:
        'Cette vente est incomplète ou incohérente et n\'a pas été enregistrée. Corrigez les champs signalés, puis validez à nouveau.',
    'unsynced-outbox':
        'Déconnexion impossible : cet appareil détient encore des ventes non synchronisées. Reconnectez-vous au réseau et attendez la fin de la synchronisation.',

    // ---- the payload itself (0004) -----------------------------------------
    'malformed-event':
        'Enregistrement illisible par le serveur. La vente est en file d\'attente — prévenez l\'administrateur et conservez le reçu papier.',
    'no-authenticated-caller':
        'Session expirée : le serveur ne sait plus qui enregistre cette vente. Reconnectez-vous, la vente sera renvoyée automatiquement.',
    'unknown-kind':
        'Type d\'opération inconnu du serveur. La vente est en file d\'attente — prévenez l\'administrateur.',
    'missing-or-invalid-id':
        'Identifiant de vente absent ou invalide. La vente est en file d\'attente — prévenez l\'administrateur.',
    'missing-amount':
        'Montant absent. La vente n\'a pas été enregistrée : ressaisissez-la avec son montant.',
    'non-numeric-amount':
        'Le montant n\'est pas un nombre. Ressaisissez la vente avec un montant en FCFA (chiffres uniquement).',
    'amount-must-be-positive-integer':
        'Le montant doit être un nombre entier de FCFA, supérieur à zéro et sans décimale. Ressaisissez la vente.',
    // Stock kinds only. Deliberately a separate code from the one above: for a
    // restock, `amount` is a valuation (|qté| × prix), not money, and 0 is legal
    // — donated stock is worth nothing and must still enter inventory (§1.5).
    // Telling an admin a restock "must be positive" would be false, and telling a
    // cashier a zero-franc sale is fine would be worse.
    'amount-must-be-non-negative-integer':
        'La valeur doit être un nombre entier de FCFA, sans décimale et jamais négative (0 est accepté pour un don). Reprenez le mouvement de stock.',
    'invalid-reverses_id':
        'La référence de la vente à corriger est invalide. Reprenez la correction depuis le reçu concerné.',
    'invalid-device_id':
        'L\'identifiant de cet appareil est invalide. Faites réenregistrer l\'appareil par un administrateur ; utilisez le carnet papier en attendant.',
    'patient-name-too-long':
        'Le nom du patient dépasse 200 caractères. Raccourcissez-le, puis validez à nouveau.',
    'reason-too-long':
        'Le motif dépasse 500 caractères. Raccourcissez-le, puis validez à nouveau.',
    'book-no-too-long':
        'Le numéro de carnet papier dépasse 40 caractères. Corrigez-le, puis validez à nouveau.',

    // ---- role and attribution (0004) ---------------------------------------
    'caller-not-a-collector':
        'Votre compte n\'est pas autorisé à encaisser. Demandez à l\'administrateur de vérifier votre rôle ; utilisez le carnet papier en attendant.',
    'caller-not-admin':
        'Cette opération est réservée à l\'administrateur. Demandez-lui de l\'effectuer.',
    'invalid-collected_by':
        'L\'identifiant de la personne qui a encaissé est invalide. Recommencez en sélectionnant l\'encaisseur dans la liste.',
    'collected_by-not-a-collector':
        'La personne indiquée comme encaisseur n\'a pas de rôle de caisse. Choisissez un caissier ou l\'administrateur.',

    // ---- device (0004) -------------------------------------------------------
    'missing-device':
        'Aucun appareil n\'est associé à cette vente. Faites enregistrer cet appareil par un administrateur ; utilisez le carnet papier en attendant.',
    'unknown-or-inactive-device':
        'Cet appareil est inconnu du serveur ou a été désactivé. Contactez l\'administrateur ; utilisez le carnet de reçus papier en attendant.',

    // ---- channel (0004) ------------------------------------------------------
    'invalid-or-missing-channel':
        'Le poste d\'encaissement (régie ou pharmacie) est absent ou invalide. Rechargez la page et ressaisissez la vente.',
    'channel-not-permitted-for-role':
        'Votre compte ne peut pas encaisser pour ce poste : la caisse A encaisse la régie, la caisse B la pharmacie. Demandez à l\'administrateur de changer votre rôle si vous assurez un remplacement.',

    // ---- line items (0004 items_problem) -------------------------------------
    'items-must-be-a-non-empty-array':
        'La vente ne contient aucune ligne. Ajoutez au moins un service ou un médicament, puis validez.',
    'invalid-item-line':
        'Une ligne de la vente est incomplète (article non identifié). Retirez-la du panier, ajoutez l\'article depuis la liste, puis validez à nouveau.',
    'item-must-be-a-drug':
        'Seuls des médicaments peuvent figurer sur un mouvement de stock. Corrigez les lignes, puis validez à nouveau.',
    'invalid-item-qty':
        'Une quantité est absente, nulle ou non entière. Corrigez les quantités du panier, puis validez à nouveau.',
    'invalid-item-unit-price':
        'Un prix unitaire n\'est pas un nombre entier de FCFA. Rechargez la liste des prix, puis ressaisissez la vente.',
    'invalid-item-line-total':
        'Un total de ligne n\'est pas un nombre entier de FCFA. Rechargez la liste des prix, puis ressaisissez la vente.',

    // ---- receipt numbers (0004) ----------------------------------------------
    'missing-receipt-no':
        'Numéro de reçu absent. La vente n\'a pas été enregistrée : utilisez le carnet de reçus papier et prévenez l\'administrateur.',
    'non-integer-receipt-no':
        'Numéro de reçu invalide. Utilisez le carnet de reçus papier et prévenez l\'administrateur.',
    'receipt-no-outside-device-blocks':
        'Ce numéro de reçu n\'appartient à aucun carnet attribué à cet appareil. Utilisez le carnet papier et prévenez l\'administrateur : la vente attend une décision.',
    'receipt-number-already-used':
        'Ce numéro de reçu a déjà été utilisé par une autre vente. La vente est conservée et placée en file d\'attente ; notez le numéro du reçu papier et prévenez l\'administrateur.',

    // ---- corrections (0004, invariant 10) ------------------------------------
    'correction-missing-target':
        'La correction ne désigne aucune vente. Reprenez-la depuis le reçu à corriger.',
    'correction-missing-reason':
        'Un motif est obligatoire pour une annulation ou une remise. Saisissez-le, puis validez à nouveau.',
    'correction-target-not-found':
        'La vente à corriger est introuvable sur le serveur — elle n\'est peut-être pas encore synchronisée. Attendez la fin de la synchronisation, puis recommencez.',
    'correction-target-not-a-sale':
        'Seule une vente peut être annulée ou remisée. Sélectionnez le reçu de la vente concernée.',
    'sale-already-voided':
        'Cette vente est déjà annulée : il n\'en reste rien à corriger. Consultez le reçu pour voir l\'annulation existante.',
    'sale-already-discounted':
        'Cette vente porte déjà une remise. Pour annuler ce qu\'il en reste, enregistrez une annulation du solde.',
    'void-amount-must-equal-sale-net-of-discount':
        'Le montant de l\'annulation doit être exactement le solde de la vente, remise déduite. Corrigez le montant, puis validez à nouveau.',
    'discount-exceeds-sale-amount':
        'Une remise doit être strictement inférieure au total de la vente. Pour une réduction totale, enregistrez une annulation.',
    'stock-adjustment-missing-reason':
        'Un motif est obligatoire pour un ajustement de stock. Saisissez-le, puis validez à nouveau.',

    // ---- database-level refusals (0004 exception handlers) --------------------
    'unknown-reference':
        'Une référence de cet enregistrement est inconnue du serveur (appareil, utilisateur ou vente d\'origine). L\'enregistrement est en file d\'attente — prévenez l\'administrateur.',
    'constraint-violation':
        'Le serveur a refusé cet enregistrement : une règle de cohérence n\'est pas respectée. Il est en file d\'attente — prévenez l\'administrateur.',
    'missing-required-field':
        'Un champ obligatoire manque à cet enregistrement. Il est en file d\'attente — prévenez l\'administrateur.',

    // ---- generic park (db/sync.js, when the server sent no reason) ------------
    conflict:
        'Le serveur a refusé cet enregistrement sans en préciser la raison. Il est conservé et placé en file d\'attente — prévenez l\'administrateur.'
});

/**
 * Split a `prefix:detail` code. Returns `null` when the code carries no tail.
 * @param {string} code
 * @returns {{prefix:string, detail:string}|null}
 */
function splitPrefixed(code) {
    const at = code.indexOf(':');
    if (at <= 0) return null;
    return { prefix: code.slice(0, at), detail: code.slice(at + 1).trim() };
}

/**
 * Whether this code has an explicit message (as opposed to the naming fallback).
 * Used by the coverage test and by callers that want to know whether they are
 * about to show a code the product has never seen.
 *
 * @param {string} code
 * @returns {boolean}
 */
export function hasMessageFor(code) {
    if (typeof code !== 'string' || code === '') return false;
    if (Object.prototype.hasOwnProperty.call(MESSAGES, code)) return true;
    const split = splitPrefixed(code);
    return !!(split && Object.prototype.hasOwnProperty.call(PREFIX_MESSAGES, split.prefix));
}

/**
 * The French sentence for a refusal code.
 *
 * NEVER returns a message that hides the code. An unmapped code is rendered with
 * the raw code inside it, because « une erreur est survenue » in front of a
 * cashier holding a patient's cash is indistinguishable from silence.
 *
 * @param {string|null|undefined} code Kebab-case code from the validation path,
 *   the collection gate, or a typed engine error.
 * @returns {string}
 */
export function messageFor(code) {
    if (typeof code !== 'string' || code.trim() === '') {
        return 'Enregistrement refusé sans code d\'erreur. Conservez le reçu papier et prévenez l\'administrateur.';
    }
    const key = code.trim();

    if (Object.prototype.hasOwnProperty.call(MESSAGES, key)) return MESSAGES[key];

    const split = splitPrefixed(key);
    if (split && Object.prototype.hasOwnProperty.call(PREFIX_MESSAGES, split.prefix)) {
        return PREFIX_MESSAGES[split.prefix](split.detail || '?');
    }

    // The fallback NAMES the rule that fired. This is the whole point.
    return `Enregistrement refusé par le serveur (code : ${key}). La vente est conservée sur cet appareil ; notez le reçu papier et transmettez ce code à l'administrateur.`;
}

/**
 * The French text for a thrown engine error, whatever its type.
 *
 * `ValidationError` carries every problem found, not just the first, so the
 * per-field detail is appended rather than swallowed.
 *
 * @param {*} error
 * @returns {string}
 */
export function messageForError(error) {
    if (!error) return messageFor(null);

    if (error.code === 'validation' && Array.isArray(error.issues) && error.issues.length > 0) {
        const fields = error.issues
            .map(i => (i && (i.field || i.code)) || '?')
            .join(', ');
        return `${MESSAGES.validation} (champs : ${fields})`;
    }

    if (typeof error.code === 'string' && error.code !== '') return messageFor(error.code);
    if (typeof error.reason === 'string' && error.reason !== '') return messageFor(error.reason);

    // Not an engine refusal at all — a transport or programmer failure. Say so
    // plainly rather than dressing it as a business rule.
    return `Échec technique : ${error.message || String(error)}. La vente n'a pas été validée — recommencez, et prévenez l'administrateur si cela se répète.`;
}

/** Every explicitly mapped code. Diagnostics and the coverage test. */
export function knownCodes() {
    return Object.keys(MESSAGES).slice();
}

/** Every mapped variable-tail prefix. */
export function knownPrefixes() {
    return Object.keys(PREFIX_MESSAGES).slice();
}
